import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { PageHeader } from "@/components/ui-kit";
import { AuthImage } from "@/components/AuthImage";
import { api, formatApiError } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

function PersonTile({ p, branch, busy, onOpen }) {
  return (
    <button data-testid={`view-as-${p.id}`} onClick={() => onOpen(p)} disabled={busy}
      className="group bg-white rounded-2xl shadow-sm p-4 flex flex-col items-center text-center gap-2 border border-transparent hover:border-violet-300 hover:-translate-y-0.5 transition-[transform,border-color]">
      <div className="h-16 w-16 rounded-full overflow-hidden bg-violet-100 flex items-center justify-center text-xl font-bold text-violet-700 ring-2 ring-white shadow">
        {busy ? <Loader2 className="animate-spin" /> : p.photo_url ? <AuthImage src={p.photo_url} alt={p.name} className="h-16 w-16 object-cover" /> : p.name.charAt(0)}
      </div>
      <div className="font-semibold text-sm text-slate-900 line-clamp-1">{p.name}</div>
      <div className="text-[11px] text-violet-700 font-medium">{ROLE_LABELS[p.role] || p.role}</div>
      <div className="text-[11px] text-slate-400 line-clamp-1">{branch || "—"}</div>
    </button>
  );
}

export default function ViewAsPage() {
  const { viewAs } = useAuth();
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [branches, setBranches] = useState([]);
  const [role, setRole] = useState("all");
  const [branchId, setBranchId] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => api.get("/view-as/people", { params: { role: role === "all" ? undefined : role, branch_id: branchId === "all" ? undefined : branchId, q: q || undefined } })
      .then((r) => setPeople(r.data)).catch((e) => toast.error(formatApiError(e))), 250);
    return () => clearTimeout(t);
  }, [role, branchId, q]);
  async function open(p) {
    setBusy(p.id);
    try { await viewAs(p.id); navigate("/"); } catch (e) { toast.error(formatApiError(e)); setBusy(null); }
  }
  const bname = (id) => branches.find((b) => b.id === id)?.name;
  const roles = Object.keys(ROLE_LABELS).filter((r) => !["master_admin", "super_admin"].includes(r));
  return (
    <AppShell>
      <PageHeader eyebrow="Admin" title="Open a Dashboard" subtitle="Tap a photo to see that person's app exactly as they see it. Look only — nothing can be changed." />
      <div className="flex flex-wrap gap-2 mb-5">
        <Input data-testid="view-as-search" placeholder="Search name, email or mobile" value={q} onChange={(e) => setQ(e.target.value)} className="w-64 bg-white" />
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="view-as-role" className="w-48 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All staff & parents</SelectItem>{roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={branchId} onValueChange={setBranchId}>
          <SelectTrigger data-testid="view-as-branch" className="w-56 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All branches</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
        </Select>
        <span data-testid="view-as-count" className="text-sm text-slate-500 self-center ml-2">{people.length} people</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {people.map((p) => <PersonTile key={p.id} p={p} branch={bname(p.branch_id)} busy={busy === p.id} onOpen={open} />)}
      </div>
      {people.length === 0 && <p className="text-sm text-slate-500">No one found.</p>}
    </AppShell>
  );
}
